import Button from "@/components/Button/Button.component";
import Image from "@/components/Image/Image.component";
import { RichText } from "@/components/RichText/RichText.component";
import { Typography } from "@/components/Typography/Typography.component";
import { cn } from "@/lib/utils";
import { ChangeEvent, useRef } from "react";
import {
  ContactFormPropsModel,
  FORM_FIELDS,
  generic_fields,
} from "./ContactForm.model";

export function ContactForm({
  topic,
  isLoading,
  error,
  handleSubmit,
}: ContactFormPropsModel) {
  const formRef = useRef<HTMLFormElement>(null);

  const invalidClasses = [
    "!border-feedback-red",
    "!text-feedback-red",
    "placeholder:!text-feedback-red",
  ];

  const classnames =
    "w-full rounded-btn border border-core-black p-2.5 text-sl leading-7 !normal-case placeholder:text-sl placeholder:uppercase placeholder:text-core-black";

  const sortFields = (fields: string[]) => {
    const sortingArr = Object.values(FORM_FIELDS) as string[];
    return fields.sort((a, b) => sortingArr.indexOf(a) - sortingArr.indexOf(b));
  };

  const resetInvalid = (e: ChangeEvent<HTMLInputElement>) => {
    e.target.classList.remove(...invalidClasses);
    const feedback = e.target.nextElementSibling;
    feedback?.classList.add("hidden");
  };

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (formRef.current) {
      if (formRef.current.checkValidity()) {
        const formData = new FormData(formRef.current);
        handleSubmit(formData);
      } else {
        const inputs: HTMLInputElement[] = Array.prototype.slice.call(
          formRef.current.getElementsByTagName("input"),
        );
        inputs.forEach(input => {
          const feedback = input.nextElementSibling;
          if (!input.checkValidity()) {
            input.classList.add(...invalidClasses);
            feedback?.classList.remove("hidden");
          } else {
            input.classList.remove(...invalidClasses);
            feedback?.classList.add("hidden");
          }
        });
      }
    }
  };

  return (
    <form onSubmit={onSubmit} noValidate ref={formRef}>
      <div className={cn("grid gap-6 pt-5", error && "opacity-30")}>
        {sortFields(topic?.requiredFormFieldsCollection || generic_fields).map(
          field => {
            if (field !== FORM_FIELDS.message) {
              return (
                <div key={field}>
                  <input
                    className={cn(
                      classnames,
                      "peer invalid:[&:not(:placeholder-shown):not(:focus)]:border-feedback-red invalid:[&:not(:placeholder-shown):not(:focus)]:text-feedback-red",
                    )}
                    onChange={resetInvalid}
                    name={Object.keys(FORM_FIELDS).find(
                      key =>
                        FORM_FIELDS[key as keyof typeof FORM_FIELDS] === field,
                    )}
                    placeholder={`${field}*`}
                    required
                    type={field === FORM_FIELDS.email ? "email" : "text"}
                    pattern={
                      field === FORM_FIELDS.email
                        ? "[a-z0-9._%+-]+@[a-z0-9.-]+.[a-z]{2,}$"
                        : "^(?!\\s*$).+"
                    }
                    disabled={!!error}
                  />
                  <Typography.BODY3 className="pt-xxs text-feedback-red hidden pl-2.5 peer-[&:not(:placeholder-shown):not(:focus):invalid]:block">
                    Please enter a valid {field.toLowerCase()}
                    {field === FORM_FIELDS.email && " address"}
                    {field === FORM_FIELDS.company && " name"}
                  </Typography.BODY3>
                </div>
              );
            } else {
              return (
                <textarea
                  key={field}
                  className={classnames}
                  name={Object.keys(FORM_FIELDS).find(
                    key =>
                      FORM_FIELDS[key as keyof typeof FORM_FIELDS] === field,
                  )}
                  placeholder="Message"
                  wrap="soft"
                  rows={5}
                  disabled={!!error}
                />
              );
            }
          },
        )}
      </div>
      {error ? (
        <div className="pt-lg flex items-center justify-between">
          <RichText
            additionalClassName={{
              paragraph: "!text-sl uppercase text-feedback-red",
              link: "!text-sl uppercase text-feedback-red",
            }}
            content={error}
          />
          <Image
            width={32}
            height={32}
            src="/static/icons/alert-icon.svg"
            alt="error"
          />
        </div>
      ) : (
        <div className="bg-core-white flex w-full items-center justify-between pt-11">
          <Button
            primary
            actionType="submit"
            disabled={isLoading}
            content={{ text: isLoading ? "SENDING ..." : "SUBMIT" }}
          />
          {isLoading && (
            <Image
              width={36}
              height={36}
              src="/static/icons/loading.gif"
              alt="loading"
              data-nimg="1"
            />
          )}
        </div>
      )}
    </form>
  );
}
